import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiJson } from "../../ingress/api";
import AdminTraceLink from "./AdminTraceLink";
import TracePanel from "./TracePanel";
import { getTenantUuid, getTenantLabel } from "../../ingress/tenant";

type Req = {
  id: string;
  tenant_id: string;
  app_id: string;
  status: string;
  run_id?: string | null;
  error?: string | null;
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  
  // Optional fields (best-effort; backend may include them depending on version)

  trace_id?: string | null;
  flow_trace_id?: string | null;
  normalized_inputs?: any;
};

function fmt(s?: string | null) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return String(s); }
}

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function StatusBadge({ status }: { status: string }) {
  const st = (status || "").toLowerCase();
  const base = "inline-flex items-center px-2 py-1 rounded border text-xs";
  if (st === "succeeded") return <span className={`${base} border-green-300 text-green-700 bg-green-50`}>succeeded</span>;
  if (st === "failed") return <span className={`${base} border-red-300 text-red-700 bg-red-50`}>failed</span>;
  if (st === "running") return <span className={`${base} border-blue-300 text-blue-700 bg-blue-50`}>running</span>;
  if (st === "queued") return <span className={`${base} border-gray-300 text-gray-700 bg-gray-50`}>queued</span>;
  return <span className={`${base} border-gray-300 text-gray-700 bg-gray-50`}>{status}</span>;
}

function safeJson(v: any) {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

type OutputResponse = {
  output_json?: any;
  redactions?: any;
  policy_summary?: any;
  run_id?: string;
  created_at?: string;
  // optional (some backends include extra fields)
  trace_id?: string | null;
  flow_trace_id?: string | null;
};

export default function AdminIngressRequestDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const tenantUuid = getTenantUuid();
  const tenantLabel = getTenantLabel();

  const [req, setReq] = useState<Req | null>(null);
  const [out, setOut] = useState<OutputResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [outLoading, setOutLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [outErr, setOutErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  async function loadRequest() {
    if (!requestId) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await apiJson<Req>(
        `/v1/admin/ingress/requests/${requestId}?tenant_id=${tenantUuid}`,
        { tenantLabel }
      );
      setReq(r);
    } catch (e: any) {
      setErr(e?.message || String(e));
      setReq(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadOutput() {
    if (!requestId) return;
    setOutLoading(true);
    setOutErr(null);
    try {
      const o = await apiJson<OutputResponse>(
        `/v1/admin/ingress/requests/${requestId}/output?tenant_id=${tenantUuid}`,
        { tenantLabel }
      );
      setOut(o);
    } catch (e: any) {
      // keep behavior: output may legitimately be missing/not ready
      setOut(null);
      setOutErr(e?.message || String(e));
    } finally {
      setOutLoading(false);
    }
  }

  async function loadAll() {
    await loadRequest();
    await loadOutput();
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const outputJson = out?.output_json ?? null;

  const extracted = useMemo(() => {
    // Normalize a few common locations for “final output”
    // (your ingress_outputs stores flow_runs.output_json which includes outputs/nodes/log).
    const outputs = outputJson?.outputs;
    const nodes = outputJson?.nodes;
    const log = Array.isArray(outputJson?.log) ? outputJson.log : [];

    // Try to find “best” display output: first output key, else whole outputs object
    let best: any = null;
    if (outputs && typeof outputs === "object") {
      const keys = Object.keys(outputs);
      if (keys.length === 1) best = outputs[keys[0]];
      else if (keys.length > 1) best = outputs;
    }

    return { outputs, nodes, log, best };
  }, [outputJson]);

  async function doCopy(label: string, value: string) {
    const ok = await copy(value);
    setCopied(ok ? label : "copy failed");
    setTimeout(() => setCopied(null), 1200);
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  if (!req) {
    return (
      <div className="p-6">
        <div className="text-sm text-red-700">Error: {err || "Not found"}</div>
        <div className="mt-3">
          <Link className="text-blue-700 hover:underline text-sm" to="/app/admin/ingress/requests">
            ← Back to Requests
          </Link>
        </div>
      </div>
    );
  }

  const canOpenApp = Boolean(req.app_id);
  const appDetailUrl = canOpenApp ? `/app/admin/ingress/apps/${req.app_id}` : "";
  const runId = req.run_id || out?.run_id || "";
  
  // Best-effort trace id resolution:
  // 1) backend may return flow_trace_id / trace_id on request detail
  // 2) backend may return normalized_inputs._ctx.trace_id on request detail
  // 3) fallback: output_json._ctx.trace_id if ever present
  const traceId =
    (req as any)?.flow_trace_id ||
    (req as any)?.trace_id ||
    (req as any)?.normalized_inputs?._ctx?.trace_id ||
    (outputJson as any)?._ctx?.trace_id ||
    null;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm text-gray-500">
            <Link className="hover:underline" to="/app/admin/ingress/requests">Ingress Requests</Link>
            <span className="mx-2">/</span>
            <span className="font-mono break-all">{req.id}</span>
          </div>

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <StatusBadge status={req.status} />
            {req.error ? (
              <span className="text-xs text-red-700 border border-red-200 bg-red-50 px-2 py-1 rounded">
                {req.error}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
            onClick={loadAll}
          >
            Refresh
          </button>

          {canOpenApp && (
            <Link
              className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
              to={appDetailUrl}
              title="Open app detail"
            >
              Open App
            </Link>
          )}
        </div>
      </div>

      {copied && (
        <div className="text-xs text-green-700">
          {copied}
        </div>
      )}

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border rounded p-3">
          <div className="text-xs text-gray-500">Request ID</div>
          <div className="mt-1 flex items-center gap-2">
            <div className="text-sm font-mono break-all">{req.id}</div>
            <button className="px-2 py-1 rounded border text-xs hover:bg-gray-50" onClick={() => doCopy("request_id copied", req.id)}>
              Copy
            </button>
          </div>
        </div>

        <div className="border rounded p-3">
          <div className="text-xs text-gray-500">App ID</div>
          <div className="mt-1 flex items-center gap-2">
            <div className="text-sm font-mono break-all">{req.app_id}</div>
            <button className="px-2 py-1 rounded border text-xs hover:bg-gray-50" onClick={() => doCopy("app_id copied", req.app_id)}>
              Copy
            </button>
          </div>
        </div>

        <div className="border rounded p-3">
          <div className="text-xs text-gray-500">Run ID</div>
          <div className="mt-1 flex items-center gap-2">
            <div className="text-sm font-mono break-all">{runId || "—"}</div>
            {runId ? (
              <button className="px-2 py-1 rounded border text-xs hover:bg-gray-50" onClick={() => doCopy("run_id copied", runId)}>
                Copy
              </button>
            ) : null}
          </div>
        </div>
        
        {/* NEW: Trace link */}
        <div className="border rounded p-3">
          <TracePanel traceId={traceId} ingressRequestId={req.id} runId={runId} />
        </div>

        <div className="border rounded p-3">
          <div className="text-xs text-gray-500">Timing</div>
          <div className="mt-1 text-sm">
            <div><span className="text-gray-500">Created:</span> {fmt(req.created_at)}</div>
            <div><span className="text-gray-500">Started:</span> {fmt(req.started_at)}</div>
            <div><span className="text-gray-500">Finished:</span> {fmt(req.finished_at)}</div>
          </div>
        </div>
      </div>

      {/* Output */}
      <div className="border rounded p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-medium">Output</div>
            <div className="text-xs text-gray-500">
              Reads from <code>/v1/admin/ingress/requests/:id/output</code> (persisted ingress_outputs).
            </div>
          </div>

          <button
            className="px-3 py-2 rounded border text-sm hover:bg-gray-50 disabled:opacity-60"
            onClick={loadOutput}
            disabled={outLoading}
          >
            {outLoading ? "Loading…" : "Fetch Output"}
          </button>
        </div>

        {outErr && (
          <div className="mt-3 text-sm text-red-700 whitespace-pre-wrap">
            {outErr}
          </div>
        )}

        {!outputJson ? (
          <div className="mt-3 text-sm text-gray-500">
            Output not ready (or not persisted yet).
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {/* Best output preview */}
            <div className="border rounded p-3 bg-white">
              <div className="text-xs text-gray-500">Best Output (derived)</div>
              <pre className="mt-2 text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-[260px]">
                {safeJson(extracted.best ?? extracted.outputs ?? outputJson)}
              </pre>
            </div>

            {/* Redactions / policy */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="border rounded p-3 bg-white">
                <div className="text-xs text-gray-500">Redactions</div>
                <pre className="mt-2 text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-[220px]">
                  {safeJson(out?.redactions ?? [])}
                </pre>
              </div>
              <div className="border rounded p-3 bg-white">
                <div className="text-xs text-gray-500">Policy Summary</div>
                <pre className="mt-2 text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-[220px]">
                  {safeJson(out?.policy_summary ?? {})}
                </pre>
              </div>
            </div>

            {/* Timeline */}
            <div className="border rounded p-3 bg-white">
              <div className="text-xs text-gray-500">Execution Log (output_json.log)</div>
              {Array.isArray(extracted.log) && extracted.log.length ? (
                <div className="mt-2 space-y-2">
                  {extracted.log.slice(0, 50).map((e: any, idx: number) => (
                    <div key={idx} className="border rounded p-2">
                      <div className="text-[11px] text-gray-600 flex flex-wrap gap-2">
                        <span className="font-mono">{e.ts || "—"}</span>
                        <span className="font-mono">{e.level || "info"}</span>
                        <span className="font-mono">{e.node_id || ""}</span>
                      </div>
                      <div className="text-sm mt-1">{e.message || ""}</div>
                      {e.extra && Object.keys(e.extra).length ? (
                        <pre className="mt-2 text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-[220px]">
                          {safeJson(e.extra)}
                        </pre>
                      ) : null}
                    </div>
                  ))}
                  {extracted.log.length > 50 ? (
                    <div className="text-xs text-gray-500">Showing first 50 log entries…</div>
                  ) : null}
                </div>
              ) : (
                <div className="mt-2 text-sm text-gray-500">No log entries found.</div>
              )}
            </div>

            {/* Raw */}
            <div className="border rounded p-3 bg-white">
              <div className="text-xs text-gray-500">Raw Response</div>
              <pre className="mt-2 text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-[520px]">
                {safeJson(out)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}